/**
 * ProviderDashboardPage duck — uses global marketplaceData entity store
 */

import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import { storableError } from '../../util/errors';
import { transitionPrivileged } from '../../util/api';
import { addMarketplaceEntities } from '../../ducks/marketplaceData.duck';
import { txCardQuery, getTxState } from '../../util/transactionCards';

// ─── Helpers ───

const entityRefs = entities =>
  entities.map(entity => ({ id: entity.id, type: entity.type }));

// States that represent a committed booking — provider has accepted and the
// transaction is moving through the rental lifecycle (or has finished). We
// exclude preauthorized/declined/expired/cancelled since those never produced
// an actual rental. Includes the paid custom-quote (negotiation) lifecycle
// so quote earnings count in the dashboard stats (2026-07-02).
const BOOKED_STATES = [
  'offer-accepted',
  'delivered',
  'changes-requested',
  'accepted',
  // v2 in-flight states (see the component's BOOKED_STATES note, 2026-07-08)
  'kit-ready',
  'pickup-issue-review',
  'active-rental',
  'pickup-pending',
  'pickup-provider-confirmed',
  'pickup-renter-confirmed',
  'picked-up',
  'return-pending',
  'return-renter-submitted',
  'return-late',
  'completed',
  'disputed',
  'resolved-partial',
  'resolved-full',
  'reviewed',
  'reviewed-by-customer',
  'reviewed-by-provider',
];

// ─── Thunks ───

export const queryOwnListings = createAsyncThunk(
  'ProviderDashboardPage/queryOwnListings',
  async (params, { extra: sdk, rejectWithValue, dispatch }) => {
    try {
      const response = await sdk.ownListings.query({
        include: ['images'],
        'fields.image': ['variants.listing-card', 'variants.listing-card-2x'],
        perPage: 100,
        ...params,
      });
      dispatch(addMarketplaceEntities(response));
      return response;
    } catch (e) {
      return rejectWithValue(storableError(e));
    }
  }
);

export const queryPendingTransactions = createAsyncThunk(
  'ProviderDashboardPage/queryPendingTransactions',
  async (params, { extra: sdk, rejectWithValue, dispatch }) => {
    try {
      const response = await sdk.transactions.query(txCardQuery({
        only: 'sale',
        // Everything awaiting the HOST's move (2026-07-02 — quote
        // requests used to be invisible here, surfacing only as inbox
        // messages): paid booking requests awaiting accept, fresh quote
        // requests awaiting an offer, and renter counter-offers.
        lastTransitions: [
          'transition/confirm-payment',
          'transition/request-quote',
          'transition/customer-make-counter-offer',
        ],
        perPage: 50,
        ...params,
      }));
      dispatch(addMarketplaceEntities(response));
      return response;
    } catch (e) {
      return rejectWithValue(storableError(e));
    }
  }
);

export const queryRecentTransactions = createAsyncThunk(
  'ProviderDashboardPage/queryRecentTransactions',
  async (params, { extra: sdk, rejectWithValue, dispatch }) => {
    try {
      const response = await sdk.transactions.query(txCardQuery({
        only: 'sale',
        // Load up to 100 so the dashboard stats (total bookings, total
        // earnings) include upcoming as well as completed rentals. The UI
        // still shows just the most recent few.
        perPage: 100,
        ...params,
      }));
      dispatch(addMarketplaceEntities(response));
      return response;
    } catch (e) {
      return rejectWithValue(storableError(e));
    }
  }
);

export const queryProviderReviews = createAsyncThunk(
  'ProviderDashboardPage/queryProviderReviews',
  async (params, { getState }) => {
    // Reviews are shown on the listing detail page, not the dashboard.
    // Skip querying them here to avoid invalid API params.
    return { data: { data: [] } };
  }
);

export const acceptTransaction = createAsyncThunk(
  'ProviderDashboardPage/acceptTransaction',
  async ({ transactionId }, { rejectWithValue }) => {
    try {
      const response = await transitionPrivileged({
        id: transactionId,
        transition: 'transition/accept',
        params: {},
      });
      return { transactionId, ...response };
    } catch (e) {
      return rejectWithValue(storableError(e));
    }
  }
);

export const declineTransaction = createAsyncThunk(
  'ProviderDashboardPage/declineTransaction',
  async ({ transactionId }, { rejectWithValue }) => {
    try {
      const response = await transitionPrivileged({
        id: transactionId,
        transition: 'transition/decline',
        params: {},
      });
      return { transactionId, ...response };
    } catch (e) {
      return rejectWithValue(storableError(e));
    }
  }
);

// ─── Slice ───

const providerDashboardPageSlice = createSlice({
  name: 'ProviderDashboardPage',
  initialState: {
    // Only store refs — entities live in global marketplaceData
    ownListingRefs: [],
    pendingTransactionRefs: [],
    recentTransactionRefs: [],
    reviewRefs: [],

    queryOwnListingsInProgress: false,
    queryOwnListingsError: null,
    queryPendingTransactionsInProgress: false,
    queryPendingTransactionsError: null,
    queryRecentTransactionsInProgress: false,
    queryRecentTransactionsError: null,

    // Stats (calculated client-side from loaded data)
    stats: {
      totalEarnings: 0,
      activeListings: 0,
      totalBookings: 0,
      averageRating: 0,
    },
    statsLoaded: false,

    // Transaction actions
    acceptInProgress: null,
    declineInProgress: null,
    transactionActionError: null,
  },
  reducers: {
    clearErrors: state => {
      state.queryOwnListingsError = null;
      state.queryPendingTransactionsError = null;
      state.queryRecentTransactionsError = null;
      state.transactionActionError = null;
    },
  },
  extraReducers: builder => {
    builder
      // Own listings
      .addCase(queryOwnListings.pending, state => {
        state.queryOwnListingsInProgress = true;
        state.queryOwnListingsError = null;
      })
      .addCase(queryOwnListings.rejected, (state, action) => {
        state.queryOwnListingsInProgress = false;
        state.queryOwnListingsError = action.payload;
      })

      // Pending transactions
      .addCase(queryPendingTransactions.pending, state => {
        state.queryPendingTransactionsInProgress = true;
        state.queryPendingTransactionsError = null;
      })
      .addCase(queryPendingTransactions.fulfilled, (state, action) => {
        state.queryPendingTransactionsInProgress = false;
        state.pendingTransactionRefs = entityRefs(action.payload.data.data);
      })
      .addCase(queryPendingTransactions.rejected, (state, action) => {
        state.queryPendingTransactionsInProgress = false;
        state.queryPendingTransactionsError = action.payload;
      })

      // Recent transactions
      .addCase(queryRecentTransactions.pending, state => {
        state.queryRecentTransactionsInProgress = true;
        state.queryRecentTransactionsError = null;
      })
      .addCase(queryRecentTransactions.fulfilled, (state, action) => {
        state.queryRecentTransactionsInProgress = false;
        state.recentTransactionRefs = entityRefs(action.payload.data.data);

        // Count any transaction the provider has committed to — accepted,
        // in-flight, or completed. Previously this only counted `complete`
        // and `review-*` transitions, so upcoming bookings (and their
        // earnings) never showed up in the dashboard totals.
        const transactions = action.payload.data.data;
        const booked = transactions.filter(t => BOOKED_STATES.includes(getTxState(t)));
        state.stats.totalBookings = booked.length;
        state.stats.totalEarnings = booked.reduce(
          (sum, t) => sum + (t?.attributes?.payoutTotal?.amount || 0),
          0
        );
        state.statsLoaded = true;
      })
      .addCase(queryRecentTransactions.rejected, (state, action) => {
        state.queryRecentTransactionsInProgress = false;
        state.queryRecentTransactionsError = action.payload;
      })

      // Reviews — for average rating
      .addCase(queryProviderReviews.fulfilled, (state, action) => {
        const reviews = action.payload.data.data;
        state.reviewRefs = entityRefs(reviews);
        const providerReviews = reviews.filter(r => r?.attributes?.type === 'ofProvider');
        state.stats.averageRating = providerReviews.length > 0
          ? providerReviews.reduce((sum, r) => sum + (r?.attributes?.rating || 0), 0) / providerReviews.length
          : 0;
      })

      // Own listings — count active
      .addCase(queryOwnListings.fulfilled, (state, action) => {
        state.queryOwnListingsInProgress = false;
        const listings = action.payload.data.data;
        state.ownListingRefs = entityRefs(listings);
        state.stats.activeListings = listings.filter(l => l?.attributes?.state === 'published').length;
      })

      // Accept/Decline
      .addCase(acceptTransaction.pending, (state, action) => {
        state.acceptInProgress = action.meta.arg.transactionId;
        state.transactionActionError = null;
      })
      .addCase(acceptTransaction.fulfilled, (state, action) => {
        state.acceptInProgress = null;
        const txId = action.payload.transactionId;
        state.pendingTransactionRefs = state.pendingTransactionRefs.filter(
          ref => ref.id.uuid !== txId
        );
      })
      .addCase(acceptTransaction.rejected, (state, action) => {
        state.acceptInProgress = null;
        state.transactionActionError = action.payload;
      })
      .addCase(declineTransaction.pending, (state, action) => {
        state.declineInProgress = action.meta.arg.transactionId;
        state.transactionActionError = null;
      })
      .addCase(declineTransaction.fulfilled, (state, action) => {
        state.declineInProgress = null;
        const txId = action.payload.transactionId;
        state.pendingTransactionRefs = state.pendingTransactionRefs.filter(
          ref => ref.id.uuid !== txId
        );
      })
      .addCase(declineTransaction.rejected, (state, action) => {
        state.declineInProgress = null;
        state.transactionActionError = action.payload;
      });
  },
});

export const { clearErrors } = providerDashboardPageSlice.actions;
export default providerDashboardPageSlice.reducer;

// ─── loadData (client-side only, no SSR) ───

export const loadData = () => () => Promise.resolve();
